'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/lib/hooks/useSubscription';
import { createClient } from '@/lib/supabase/client';

interface Plan {
  id: string;
  name: string;
  price: number;
  description: string;
  highlight: boolean;
  badge?: string;
  features: string[];
  cta: string;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    description: 'Start your mental wellbeing journey at no cost.',
    highlight: false,
    features: [
      '20 AI conversations / month',
      'Basic mood check-ins',
      'Journal entries (up to 10)',
      'Guided sessions (up to 3)',
      'Crisis support alerts',
    ],
    cta: 'Get Started Free',
  },
  {
    id: 'plus',
    name: 'Plus',
    price: 149,
    description: 'For those who want deeper support every day.',
    highlight: true,
    badge: 'Most Popular',
    features: [
      'Unlimited AI conversations',
      'Unlimited journal entries',
      'Unlimited guided sessions',
      'Mood history & tracking',
      'Weekly reflection summaries',
      'Voice input (speech-to-text)',
      'Multi-language support',
    ],
    cta: 'Start Free Trial',
  },
];

const PLAN_ORDER: Record<string, number> = { free: 0, plus: 1 };

export default function PricingPage() {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [successPlan, setSuccessPlan] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { user } = useAuth();
  const router = useRouter();
  const subscription = useSubscription();

  // Auto-redirect to app after successful trial signup
  useEffect(() => {
    if (!successPlan) return;
    const timer = setTimeout(() => {
      router.push('/home-chat-interface?trial=started');
    }, 2000);
    return () => clearTimeout(timer);
  }, [successPlan, router]);

  const currentPlanId = user ? subscription.plan : null;
  const currentStatus = user ? subscription.status : null;
  const isActiveSub = currentStatus === 'active' || currentStatus === 'trial';

  const getPlanCta = (plan: Plan): string => {
    if (!user) return plan.id === 'free' ? 'Get Started Free' : plan.cta;
    if (currentPlanId === plan.id && isActiveSub) return 'Current Plan';
    if (currentPlanId && PLAN_ORDER[plan.id] > PLAN_ORDER[currentPlanId]) return 'Upgrade';
    if (plan.id === 'free') return 'Get Started Free';
    return plan.cta;
  };

  const isCurrentPlan = (plan: Plan): boolean =>
    !!user && currentPlanId === plan.id && isActiveSub;

  const handleSelectPlan = async (plan: Plan) => {
    if (isCurrentPlan(plan)) return;

    if (plan.id === 'free') {
      window.location.href = '/sign-up-login-screen?tab=register';
      return;
    }

    if (!user) {
      window.location.href = `/sign-up-login-screen?tab=login&redirect=/pricing`;
      return;
    }

    setLoadingPlan(plan.id);
    setErrorMsg(null);

    try {
      const supabase = createClient();

      // Remove any existing active/trial subscription first
      await supabase
        .from('user_subscriptions')
        .delete()
        .eq('user_id', user.id)
        .in('status', ['trial', 'active']);

      const trialEndsAt = new Date();
      trialEndsAt.setMonth(trialEndsAt.getMonth() + 2);

      const { error } = await supabase.from('user_subscriptions').insert({
        user_id: user.id,
        plan: plan.id,
        billing_cycle: 'monthly',
        status: 'trial',
        price_at_signup: plan.price,
        trial_starts_at: new Date().toISOString(),
        trial_ends_at: trialEndsAt.toISOString(),
        payment_due_at: trialEndsAt.toISOString(),
      });

      if (error) throw error;

      setSuccessPlan(plan.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setErrorMsg(msg);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Topbar */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong border-b border-[var(--glass-border)]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-bold text-xl gradient-text tracking-tight">Uyir</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link href="/home-chat-interface" className="px-5 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 transition-all">
                Go to App
              </Link>
            ) : (
              <>
                <Link href="/sign-up-login-screen?tab=login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Sign in
                </Link>
                <Link href="/sign-up-login-screen?tab=register" className="px-5 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 transition-all">
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pt-28 pb-24 px-4">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[var(--secondary)] text-[var(--secondary-foreground)] mb-4 tracking-wide uppercase">
            Simple Pricing
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
            Your mental wellbeing,{' '}
            <span className="gradient-text">your pace</span>
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Start free. Upgrade when you&apos;re ready. Plus plan includes a{' '}
            <strong className="text-foreground">2-month free trial</strong> — no card required upfront.
          </p>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div className="max-w-md mx-auto mb-8 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm text-center">
            {errorMsg}
          </div>
        )}

        {/* Success Message */}
        {successPlan && (
          <div className="max-w-lg mx-auto mb-8 px-5 py-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm text-center space-y-1">
            <p className="font-semibold text-base">
              🎉 You&apos;re on the {plans.find(p => p.id === successPlan)?.name} plan!
            </p>
            <p>
              Your 2-month free trial has started. No payment needed right now — we&apos;ll reach out before your trial ends.
            </p>
            <Link
              href="/home-chat-interface"
              className="inline-block mt-2 px-5 py-2 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 transition-all"
            >
              Go to App →
            </Link>
          </div>
        )}

        {/* Plans Grid */}
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {plans.map((plan) => {
            const isLoading = loadingPlan === plan.id;
            const isCurrent = isCurrentPlan(plan);
            const ctaLabel = getPlanCta(plan);
            const isUpgrade = !!user && !!currentPlanId && PLAN_ORDER[plan.id] > PLAN_ORDER[currentPlanId] && isActiveSub;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl flex flex-col transition-all duration-300 ${
                  plan.highlight
                    ? 'gradient-bg text-white shadow-2xl scale-[1.03] ring-2 ring-purple-400/40'
                    : 'glass border border-[var(--glass-border)] hover:shadow-lg'
                } ${isCurrent ? 'ring-2 ring-emerald-400/50' : ''}`}
              >
                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-emerald-500 text-white shadow-md">
                    ✓ Current Plan
                  </div>
                )}

                {/* Promo Badge (only if not current plan) */}
                {plan.badge && !isCurrent && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    plan.highlight ? 'bg-white text-[var(--primary)]' : 'gradient-bg text-white'
                  }`}>
                    {plan.badge}
                  </div>
                )}

                <div className="p-7 flex flex-col flex-1">
                  {/* Plan name */}
                  <div className="mb-5">
                    <h2 className={`text-xl font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-foreground'}`}>
                      {plan.name}
                    </h2>
                    <p className={`text-sm leading-relaxed ${plan.highlight ? 'text-white/80' : 'text-muted-foreground'}`}>
                      {plan.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    {plan.price === 0 ? (
                      <div className={`text-4xl font-extrabold ${plan.highlight ? 'text-white' : 'text-foreground'}`}>
                        Free
                      </div>
                    ) : (
                      <div className="flex items-end gap-1">
                        <span className={`text-sm font-medium mt-1 ${plan.highlight ? 'text-white/70' : 'text-muted-foreground'}`}>₹</span>
                        <span className={`text-4xl font-extrabold leading-none ${plan.highlight ? 'text-white' : 'text-foreground'}`}>
                          {plan.price}
                        </span>
                        <span className={`text-sm mb-1 ${plan.highlight ? 'text-white/70' : 'text-muted-foreground'}`}>
                          /mo
                        </span>
                      </div>
                    )}
                    {plan.id !== 'free' && (
                      <p className={`text-xs mt-1 font-medium ${plan.highlight ? 'text-white/80' : 'text-[var(--primary)]'}`}>
                        Free for 2 months · Pay after trial
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <svg
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-white' : 'text-[var(--primary)]'}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className={`text-sm ${plan.highlight ? 'text-white/90' : 'text-foreground'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isLoading || isCurrent}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-95 disabled:cursor-not-allowed ${
                      isCurrent
                        ? plan.highlight
                          ? 'bg-white/20 text-white/70 cursor-default' :'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 cursor-default'
                        : isUpgrade
                        ? plan.highlight
                          ? 'bg-white text-[var(--primary)] hover:bg-white/90 shadow-md'
                          : 'gradient-bg text-white hover:opacity-90'
                        : plan.highlight
                        ? 'bg-white text-[var(--primary)] hover:bg-white/90 shadow-md disabled:opacity-60'
                        : 'gradient-bg text-white hover:opacity-90 disabled:opacity-60'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving…
                      </span>
                    ) : isUpgrade ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        {ctaLabel}
                      </span>
                    ) : (
                      ctaLabel
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Billing cycle note for logged-in users */}
        {user && isActiveSub && (
          <p className="text-center text-xs text-muted-foreground mt-6">
            Upgrading will start a new 2-month trial on the Plus plan. Your current plan will be replaced immediately.
          </p>
        )}

        {/* Trust strip */}
        <div className="max-w-4xl mx-auto mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            { icon: '🎁', title: '2 Months Free', desc: 'Full access during trial — no credit card needed' },
            { icon: '🌐', title: '6 Languages', desc: 'English, Tamil, Hindi, Kannada, Telugu & Malayalam' },
            { icon: '🇮🇳', title: 'Made for India', desc: 'Prices designed for the Indian market' },
            { icon: '↩️', title: 'Cancel Anytime', desc: 'No lock-in. Pause or cancel whenever you want' },
          ].map((item) => (
            <div key={item.title} className="glass rounded-2xl p-5 border border-[var(--glass-border)]">
              <div className="text-2xl mb-2">{item.icon}</div>
              <div className="font-semibold text-foreground text-sm mb-1">{item.title}</div>
              <div className="text-xs text-muted-foreground">{item.desc}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
